import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Crown, Send, Lock, Heart, Sparkles, 
  MessageCircle, Image as ImageIcon, Smile
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Navbar from '../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GoldChat() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  // ELLA'S EIGEN PROFIELFOTO - DEFINITIEF HARDCODED - NOOIT WIJZIGEN
  const ellaProfileImage = `${process.env.REACT_APP_BACKEND_URL}/api/media/85496322-f571-428b-a286-fba5d76f6094`; // IMG_5340.jpeg
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Skip loading image from database - use hardcoded instead
  useEffect(() => {
  }, []);

  useEffect(() => {
    // Wait for auth to load
    if (authLoading) return;
    
    if (!user) {
      navigate('/login');
      return;
    }

    // Check if user has gold access
    const checkAccess = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const loyalty = res.data?.loyalty_tier || 'bronze';
        const isGold = loyalty === 'gold' || loyalty === 'diamond' || res.data?.is_admin;
        setHasAccess(isGold);

        if (isGold) {
          // Load chat history
          const historyRes = await axios.get(`${API}/chat/gold/history`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (historyRes.data) {
            const formattedHistory = historyRes.data.reverse().flatMap(chat => [
              { type: 'user', text: chat.user_message, time: chat.created_at },
              { type: 'ella', text: chat.ella_response, image: chat.image_url, time: chat.created_at }
            ]);
            setMessages(formattedHistory);
          }
        }
      } catch (error) {
        console.error('Access check error:', error);
        setHasAccess(false);
      } finally {
        setCheckingAccess(false);
      }
    };

    checkAccess();
  }, [user, authLoading, navigate]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { type: 'user', text: userMessage, time: new Date().toISOString() }]);
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/chat/gold`, {
        message: userMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessages(prev => [...prev, {
        type: 'ella',
        text: res.data.response,
        image: res.data.image_url,
        time: new Date().toISOString()
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      if (error.response?.status === 403) {
        toast.error('Gold membership required for chat');
        setHasAccess(false);
      } else {
        toast.error('Failed to send message');
      }
    } finally {
      setLoading(false);
    }
  };

  if (checkingAccess) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center">
        <div className="animate-pulse text-[#D4AF37]">Loading...</div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)]">
        <Navbar />
        <div className="pt-32 px-6 flex flex-col items-center justify-center text-center">
          <div className="w-24 h-24 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mb-6">
            <Lock className="w-12 h-12 text-[#D4AF37]" />
          </div>
          <h1 className="text-3xl font-medium mb-4">
            <span className="text-[#D4AF37]">Gold</span> {t('goldChat.membersOnly') || 'Members Only'}
          </h1>
          <p className="text-[var(--text-secondary)] max-w-md mb-8">
            {t('goldChat.upgradeMessage') || 'Upgrade to Gold or Diamond membership to chat privately with Ella. She\'s waiting to hear from you... 💋'}
          </p>
          <Button 
            onClick={() => navigate('/packages')}
            className="bg-[#D4AF37] hover:bg-[#B8963A] text-black px-8 py-6 text-lg"
          >
            <Crown className="w-5 h-5 mr-2" />
            {t('goldChat.upgradeNow') || 'Upgrade to Gold'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex flex-col">
      <Navbar />
      
      <div className="flex-1 pt-20 pb-4 px-4 max-w-4xl mx-auto w-full flex flex-col">
        {/* Header */}
        <div className="glass rounded-xl p-4 mb-4 flex items-center gap-4 border border-[#D4AF37]/30">
          <div className="relative">
            <img 
              src={ellaProfileImage}
              alt="Ella Gold"
              className="w-14 h-14 rounded-full object-cover border-2 border-[#D4AF37]"
            />
            <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-[var(--bg-primary)]" />
          </div>
          <div className="flex-1">
            <h2 className="font-medium text-lg flex items-center gap-2 text-[var(--text-primary)]">
              Ella Gold
              <Crown className="w-4 h-4 text-[#D4AF37]" />
            </h2>
            <p className="text-sm text-green-400">{t('goldChat.online') || 'Online now'}</p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1 bg-[#D4AF37]/20 rounded-full">
            <Sparkles className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-sm text-[#D4AF37]">{t('goldChat.vipChat') || 'VIP Chat'}</span>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 glass rounded-xl p-4 mb-4 overflow-y-auto border border-[var(--border-color)]" style={{ maxHeight: 'calc(100vh - 280px)' }}>
          {messages.length === 0 && (
            <div className="text-center py-12">
              <Heart className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2 text-[var(--text-primary)]">{t('goldChat.startChat') || 'Start chatting with Ella'}</h3>
              <p className="text-[var(--text-secondary)] text-sm">
                {t('goldChat.startMessage') || 'Say hi! Ella is excited to chat with you... 😘'}
              </p>
            </div>
          )}

          {messages.map((msg, idx) => (
            <div 
              key={idx}
              className={`mb-4 flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[80%] ${msg.type === 'user' ? 'order-1' : 'order-2'}`}>
                {msg.type === 'ella' && (
                  <div className="flex items-center gap-2 mb-1">
                    <img 
                      src={ellaProfileImage}
                      alt="Ella"
                      className="w-6 h-6 rounded-full object-cover"
                    />
                    <span className="text-xs text-[var(--text-secondary)]">Ella</span>
                  </div>
                )}
                <div 
                  className={`p-4 rounded-2xl ${
                    msg.type === 'user' 
                      ? 'bg-[#D4AF37] text-black rounded-br-sm' 
                      : 'bg-[var(--bg-secondary)] text-[var(--text-primary)] rounded-bl-sm border border-[var(--border-color)]'
                  }`}
                >
                  <p>{msg.text}</p>
                  {msg.image && (
                    <img 
                      src={msg.image}
                      alt="From Ella"
                      className="mt-3 rounded-lg max-w-full"
                    />
                  )}
                </div>
                <span className="text-xs text-[var(--text-secondary)] mt-1 block">
                  {new Date(msg.time).toLocaleTimeString()}
                </span>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start mb-4">
              <div className="bg-[var(--bg-secondary)] p-4 rounded-2xl rounded-bl-sm border border-[var(--border-color)]">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="glass rounded-xl p-3 flex items-center gap-3 border border-[var(--border-color)]">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder={t('goldChat.placeholder') || 'Type a message to Ella...'}
            className="flex-1 bg-transparent border-none focus:ring-0 text-[var(--text-primary)] placeholder:text-[var(--text-secondary)]"
            disabled={loading}
          />
          <Button
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            className="bg-[#D4AF37] hover:bg-[#B8963A] text-black p-3 rounded-full"
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
